import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../components/landing/landing.css';
import toast from 'react-hot-toast';

export default function Login() {
  const { login }      = useAuth();
  const navigate       = useNavigate();
  const [form, setForm] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate('/app');
    } catch (err) {
      const body = err.response?.data;
      if (err.response?.status === 403 && body?.requires_verification) {
        toast('Confirm your email to continue', { icon: '📬' });
        navigate('/verify-otp', {
          state: { email: body.email || form.email, devOtp: body.dev_otp || '' },
        });
        return;
      }
      toast.error(body?.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="cz-root">
      <div className="cz-grain" aria-hidden="true" />

      <div className="cz-auth">
        {/* ── LEFT: certificate / editorial panel ─────────────────── */}
        <aside className="cz-auth-side">
          <Link to="/" className="cz-wordmark">
            <span className="cz-wordmark-c">C</span>alibZa
            <sup className="cz-wordmark-tm">®</sup>
          </Link>

          <dl className="cz-auth-meta">
            <dt>Document</dt><dd>Auth-01 · Operator sign-in</dd>
            <dt>Issued</dt><dd>{today}</dd>
            <dt>Jurisdiction</dt><dd>US · CA · UK · EU · IN · AU</dd>
          </dl>

          <h1 className="cz-auth-headline">
            Return to the <em>instrument.</em>
          </h1>

          <p className="cz-auth-lede">
            Your calibration workspace is waiting. Sign in to resume the review
            cycle, audit distribution drift, or countersign a PIP.
          </p>

          <div className="cz-auth-stamp">
            <div className="cz-auth-stamp-head">
              <span>Tenant snapshot</span>
              <strong>In good standing</strong>
            </div>
            <div className="cz-auth-stamp-rows">
              <span className="k">Data residency</span><span className="bar" /><span className="v">us-east-1 · encrypted</span>
              <span className="k">Session policy</span><span className="bar" /><span className="v">24h rolling token</span>
              <span className="k">SSO</span><span className="bar" /><span className="v">Google · Okta · Azure AD</span>
              <span className="k">Audit log</span><span className="bar" /><span className="v">Immutable · 7 yr</span>
            </div>
          </div>
        </aside>

        {/* ── RIGHT: form ─────────────────────────────────────────── */}
        <main className="cz-auth-panel">
          <div className="cz-auth-record">
            <span>Serial № <strong>CZ-2026-04-14</strong></span>
            <span>Rev. A · Internal</span>
          </div>

          <div className="cz-auth-panel-inner">
            <header className="cz-auth-panel-head">
              <span className="cz-mono-label">§ 01 · Credentials</span>
              <h2>Sign in to your <em>workspace.</em></h2>
              <p>Use your work email. Your session is signed, encrypted, and logged.</p>
            </header>

            <form className="cz-auth-form" onSubmit={handleSubmit} autoComplete="on">
              <div className="cz-field">
                <label htmlFor="email" className="cz-field-label">
                  <span className="cz-field-label-num">01</span>
                  <span>Work email</span>
                </label>
                <input
                  id="email" name="email" type="email" required autoFocus
                  autoComplete="email"
                  className="cz-input"
                  placeholder="jane@acme.com"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                />
              </div>

              <div className="cz-field">
                <label htmlFor="password" className="cz-field-label">
                  <span className="cz-field-label-num">02</span>
                  <span>Password</span>
                  <Link to="/forgot" className="cz-field-label-hint">Forgot?</Link>
                </label>
                <input
                  id="password" name="password" type="password" required
                  autoComplete="current-password"
                  className="cz-input"
                  placeholder="• • • • • • • •"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                />
              </div>

              <button type="submit" disabled={loading} className="cz-auth-submit">
                {loading ? 'Verifying credentials…' : 'Sign in'}
                <span className="cz-btn-arrow">→</span>
              </button>
            </form>

            <div className="cz-auth-alt">
              <span>No workspace yet?</span>
              <Link to="/register">Start a free pilot →</Link>
            </div>

            <p className="cz-auth-legal">
              Protected by session-bound tokens · <Link to="/privacy">Privacy</Link> · <Link to="/terms">Terms</Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
